import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ScrollableTabs from 'components/Shared/ScrollableTabs/Index'
import Icon from 'components/Shared/Icon/Index'

import colors from 'components/utils/colors'
import { capitalizeFirstLetter } from 'utils/strings'


import {
  RouteTicketProviderTickets,
  RouteTicketProviderTicket,
  RouteTicketingSystemsTickets,
  RouteTicketingSystemsTicket,
} from 'routes'

const StyledWrapper = styled.div`
  position: relative;
  top: 1px;
  right: 0;
  left: 0;
  background: ${colors.secondary98};
  border-bottom: 1px solid ${colors.secondary70};
  padding: 0 8px;
`

const NoDataContent = styled.div`
  display: inline-flex;
  align-items: center;
  height: 24px;
  gap: 4px
`

const NoTicketingSystemsText = styled.div`
  font-size: 10px;
  color: ${colors.secondary40}
`

const getTabItems = (relatedSystems, t) => {
  const { ticketProvider, ticketingSystems } = relatedSystems
  const items = []
  let ticketProviderOrTicketUrl
  if (ticketProvider.externalId) {
    ticketProviderOrTicketUrl = RouteTicketProviderTicket({
      ticketProviderId: ticketProvider.id,
      ticketId: ticketProvider.externalId,
    })
  } else {
    ticketProviderOrTicketUrl = RouteTicketProviderTickets({
      ticketProviderId: ticketProvider.id,
    })
  }
  items.push({
    title: ticketProvider.name,
    badge: t('scrollableTabsPanel.provider'),
    to: ticketProviderOrTicketUrl,
  })

  ticketingSystems.forEach(ts => {
    let ticketingSystemOrTicketUrl
    if (ts.externalId) {
      ticketingSystemOrTicketUrl = RouteTicketingSystemsTicket({
        ticketingSystemDisplayId: ts.id,
        ticketId: ts.externalId,
      })
    } else {
      ticketingSystemOrTicketUrl = RouteTicketingSystemsTickets({
        ticketingSystemDisplayId: ts.id,
      })
    }
    items.push({
      title: ts.name,
      badge: capitalizeFirstLetter(ts.partnerName),
      to: ticketingSystemOrTicketUrl,
    })
  })

  return items
}

const ScrollableTabsPanel = ({ relatedSystems, ticketingSystemDisplayId }) => {
  const { t } = useTranslation()

  if (!relatedSystems) {
    return null
  }

  const { ticketProvider, ticketingSystems } = relatedSystems

  const tabItems = getTabItems(relatedSystems, t)
  const tsIdx = ticketingSystems.findIndex(ts => ts.id === ticketingSystemDisplayId)
  const activeTabIndex = (tsIdx === -1) ? 0 : tsIdx + 1
  if (ticketProvider && ticketingSystems.length === 0) {
    return (
      <StyledWrapper>
        <NoDataContent>
          <Icon iconName='icon-delete-icon-16' color={colors.secondary40} />
          <NoTicketingSystemsText>{t('scrollableTabsPanel.noTicketingSystems')}</NoTicketingSystemsText>
        </NoDataContent>
      </StyledWrapper>
    )
  }

  return (
    <StyledWrapper>
      <ScrollableTabs items={tabItems} activeTabIndex={activeTabIndex} />
    </StyledWrapper>
  )
}

ScrollableTabsPanel.propTypes = {
  relatedSystems: PropTypes.object,
  ticketingSystemDisplayId: PropTypes.string,
}

export default ScrollableTabsPanel
